import { ReactNode, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import {
  FilterDialogV2,
  FilterDialogV2Props,
} from 'src/components/FilterToolbar/FilterDialogV2/FilterDialogV2';
import { Content } from 'src/contexts/ContentContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { ToolbarButton } from 'src/core/POS/ToolbarButton';
import { vars } from 'src/core/themes';
import { Popover } from 'src/core/ui';
import { ScrollableToolbar } from 'src/core/ui/ScrollableToolbar';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { FilterIcon, IconsFill } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';

import {
  FilterToolbarGroup,
  FilterToolbarItemId,
} from '../Filters/Filter.types';
import {
  CustomFilterButton,
  CustomFilterButtonProps,
} from './CustomFilterButton';
import { CustomFilterDialog } from './CustomFilterButton/CustomFilterDialog';
import { FilterDialog } from './FilterDialog';
import * as styles from './FilterToolbar.css';
import { MobileFilterToolbar } from './MobileFilterToolbar';
import { QuickFilterToolbarItem } from './QuickFilterToolbarItem';

export type FilterToolbarProps = {
  filters: FilterToolbarGroup[];
  quickFilters?: QuickFilterToolbarItem[];
  /**
   * Extra chips rendered right after the "All" filter, ahead of `quickFilters`.
   * Unlike `quickFilters` these are not managed by the custom-filter UI — use for
   * standalone toggles (e.g. a client-side filter) that shouldn't be reordered,
   * hidden, favorited or deleted.
   */
  additionalQuickFilters?: QuickFilterToolbarItem[];
  /**
   * Whether the "All" quick filter is currently selected.
   */
  isAllSelected?: boolean;
  onResetAll: () => void;
  resetTempQuery?: (keys?: FilterToolbarItemId[]) => void; // TODO: make this not optional
  tempQuery?: Partial<Record<FilterToolbarItemId, unknown>>;
  setTempQuery?: (query: Partial<Record<FilterToolbarItemId, unknown>>) => void;
  onSubmitFilter?: () => void;
  filterAppliedCounts: number;
  filterAppliedIds?: FilterToolbarItemId[];
  mandatoryFiltersToShow?: FilterToolbarItemId[];
  allContentId?: ContentId;
  customFilterActions?: ReactNode;
  showAllButton?: boolean;
  showCustomFilterButton?: boolean;
  onCreateCustomQuickFilter?: (filter: { name: string }) => void;
  onUpdateCustomQuickFilter?: (
    id: string,
    updatedFilter: { name: string }
  ) => void;
  filterAppliedMessage?: React.ReactNode;

  alwaysShowAllFilters?: boolean;
} & Pick<
  CustomFilterButtonProps,
  | 'onToggleQuickFilter'
  | 'onMoveQuickFilter'
  | 'onDeleteCustomQuickFilter'
  | 'favoriteQuickFilterId'
  | 'onToggleFavoriteQuickFilter'
> &
  Pick<
    FilterDialogV2Props,
    | 'onEditabilityChange'
    | 'embeddedDisplayOnly'
    | 'performerVenueFilter'
    | 'listView'
  >;

export function FilterToolbar({
  filters,
  quickFilters,
  additionalQuickFilters,
  isAllSelected,
  allContentId,
  onResetAll,
  onSubmitFilter,
  filterAppliedCounts,
  showAllButton = true,
  showCustomFilterButton = true,
  onCreateCustomQuickFilter,
  onUpdateCustomQuickFilter,
  filterAppliedIds,
  resetTempQuery,
  tempQuery,
  setTempQuery,
  mandatoryFiltersToShow = [],
  alwaysShowAllFilters,
  embeddedDisplayOnly = false,
  performerVenueFilter,
  filterAppliedMessage,
  onEditabilityChange,
  listView,
  customFilterActions,
  ...customFilterButtonProps
}: FilterToolbarProps) {
  const customFilterDialog = useBasicDialog();
  // the quick filter currently being edited
  const [currQuickFilterId, setCurrQuickFilterId] = useState<string | null>(
    null
  );
  const currQuickFilter = quickFilters?.find(
    ({ id }) => id === currQuickFilterId
  );

  const isMobile = useMatchMedia('mobile');
  const allFilter = useMemo(
    () =>
      showAllButton
        ? [
            {
              id: 'all',
              name: <Content id={allContentId ?? ContentId.All} />,
              isHidden: false,
              isSelected: isAllSelected,
              onClick: onResetAll,
              isCustomFilter: false,
            },
          ]
        : [],
    [allContentId, isAllSelected, onResetAll, showAllButton]
  );

  const mergedFilters = useMemo(
    () => [
      ...allFilter,
      ...(additionalQuickFilters ?? []),
      ...(quickFilters ?? []),
    ],
    [allFilter, additionalQuickFilters, quickFilters]
  );

  const { initialQuery } = useFilterQueryContext();

  const hasFiltersApplied = useMemo(
    () => !isEqual(tempQuery, initialQuery),
    [initialQuery, tempQuery]
  );

  const onSaveQuickFilter = useMemo(
    () =>
      showCustomFilterButton
        ? () => {
            customFilterDialog.launchDialog();
          }
        : undefined,
    [customFilterDialog, showCustomFilterButton]
  );

  if (embeddedDisplayOnly || listView) {
    return (
      <FilterDialogV2
        filters={filters}
        onResetAll={onResetAll}
        onSubmitFilter={onSubmitFilter}
        appliedFilterIds={filterAppliedIds || []}
        onSaveQuickFilter={onSaveQuickFilter}
        customFilterActions={customFilterActions}
        resetTempQuery={resetTempQuery}
        tempQuery={tempQuery}
        setTempQuery={setTempQuery}
        initialQuery={initialQuery}
        mandatoryFiltersToShow={mandatoryFiltersToShow}
        filterAppliedMessage={filterAppliedMessage}
        onEditabilityChange={onEditabilityChange}
        embeddedDisplayOnly={embeddedDisplayOnly}
        listView={listView}
        performerVenueFilter={performerVenueFilter}
      />
    );
  }

  return (
    <div className={styles.filterToolbarContainer}>
      {isMobile && <MobileFilterToolbar filters={mergedFilters} />}
      {filters.length > 0 && (
        <Popover.Root
          onOpenChange={(isOpen) => {
            if (!alwaysShowAllFilters && !isOpen) {
              resetTempQuery?.();
            }
          }}
        >
          <Popover.Trigger asChild>
            <div className={styles.filterToolbarIcon}>
              <FilterIcon
                title="filter"
                fill={
                  hasFiltersApplied
                    ? IconsFill.textBrand
                    : IconsFill.textPrimary
                }
                size={vars.iconSize.m}
                withHoverEffect
              />
              <div className={styles.filterCountLabel}>
                <Content id={ContentId.Filters} />
              </div>
              {hasFiltersApplied && filterAppliedCounts > 0 && (
                <div className={styles.filterCount}>{filterAppliedCounts}</div>
              )}
            </div>
          </Popover.Trigger>
          <Popover.Content
            align="start"
            style={{
              maxHeight: 'var(--radix-popover-content-available-height)',
              overflowY: 'auto',
            }}
          >
            {!alwaysShowAllFilters ? (
              <FilterDialogV2
                filters={filters}
                onResetAll={onResetAll}
                onSubmitFilter={onSubmitFilter}
                appliedFilterIds={filterAppliedIds || []}
                onSaveQuickFilter={onSaveQuickFilter}
                customFilterActions={customFilterActions}
                resetTempQuery={resetTempQuery}
                tempQuery={tempQuery}
                setTempQuery={setTempQuery}
                initialQuery={initialQuery}
                mandatoryFiltersToShow={mandatoryFiltersToShow}
                filterAppliedMessage={filterAppliedMessage}
                onEditabilityChange={onEditabilityChange}
              />
            ) : (
              <FilterDialog
                hasFiltersApplied={hasFiltersApplied}
                onResetAll={onResetAll}
                onSubmitFilter={onSubmitFilter}
                filters={filters}
                onSaveQuickFilter={onSaveQuickFilter}
                filterAppliedMessage={filterAppliedMessage}
              />
            )}
          </Popover.Content>
        </Popover.Root>
      )}
      {!isMobile && (
        <ScrollableToolbar>
          {mergedFilters?.map(({ id, name, isHidden, isSelected, onClick }) => {
            if (isHidden) {
              return null;
            }
            return (
              <ToolbarButton key={id} isSelected={isSelected} onClick={onClick}>
                {name}
              </ToolbarButton>
            );
          })}
        </ScrollableToolbar>
      )}
      {showCustomFilterButton && (
        <>
          <CustomFilterButton
            quickFilters={quickFilters ?? []}
            {...customFilterButtonProps}
            onCreateCustomQuickFilter={() => {
              customFilterDialog.launchDialog();
            }}
            onUpdateCustomQuickFilter={(id) => {
              setCurrQuickFilterId(id);
              customFilterDialog.launchDialog();
            }}
          />
          {customFilterDialog.dialogProps.isOpen && (
            <CustomFilterDialog
              {...customFilterDialog.dialogProps}
              headerTitle={
                currQuickFilterId ? (
                  <Content id={ContentId.EditCustomFilter} />
                ) : (
                  <Content id={ContentId.CreateCustomFilter} />
                )
              }
              defaultValues={
                currQuickFilter && currQuickFilter.isCustomFilter
                  ? {
                      filterName: currQuickFilter.name as string,
                    }
                  : undefined
              }
              validateIsFilterUnique={(val) => {
                if (currQuickFilterId && val === currQuickFilterId) {
                  return true;
                }
                return (
                  quickFilters === undefined ||
                  !quickFilters.some(({ id }) => id === val)
                );
              }}
              onSave={(data) => {
                if (currQuickFilterId) {
                  onUpdateCustomQuickFilter?.(currQuickFilterId, {
                    name: data.filterName,
                  });
                } else {
                  onCreateCustomQuickFilter?.({ name: data.filterName });
                }
                setCurrQuickFilterId(null);
                customFilterDialog.closeDialog();
              }}
              onCancel={() => {
                setCurrQuickFilterId(null);
                customFilterDialog.closeDialog();
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
